import { inject, Injectable, signal } from '@angular/core';
import { ColumnType } from '@app/components/shared/column-visibility/column-visibility.component';
import { EntityTypes } from '@app/models/enums/EntityTypes';
import { ColumnState, GridApi } from 'ag-grid-enterprise';
import { ObjectExtensionsService } from 'ngx-lib';
import { lastValueFrom } from 'rxjs';
import { GridService } from './grid.service';
import { EntityTypeClient } from './http/clients/api-proxies';

@Injectable({
    providedIn: 'root'
})
export class ColumnVisibilityService {
    gridApi: GridApi | null = null;
    availableColumns = signal<ColumnType[]>([]);
    columnVisibilityState = signal<ColumnType[]>([]);
    lastAppliedState = signal<ColumnType[]>([]);

    private readonly entityTypeClient = inject(EntityTypeClient);
    private readonly objectExtensionService = inject(ObjectExtensionsService);
    private readonly gridService = inject(GridService);

    setGridApi(api: any) {
        this.gridApi = api;
        const currentState = this.columnVisibilityState();

        if (currentState.length > 0 && this.gridApi) {
            const columnState: ColumnState[] = this.gridApi.getColumnState();

            for (const state of columnState) {
                const columnFound = currentState.find((column) => column.propertyName === state.colId);
                if (columnFound) {
                    state.hide = !columnFound.isSelected;
                }
            }

            this.gridApi.applyColumnState({ state: columnState });
        }
    }

    async initColumnVisibilityState(entityType: EntityTypes) {
        if (this.availableColumns().length > 0) {
            return;
        }

        const cols = await lastValueFrom(this.entityTypeClient.entityTypeAvailableColumns(entityType));
        const availableColumns = cols.map((col) => ({
            ...col,
            isSelected: true
        }));

        this.availableColumns.set(availableColumns);
        this.setColumnVisibilityState(availableColumns);
        this.lastAppliedState.set(this.objectExtensionService.clone(availableColumns) as ColumnType[]);
    }

    updateGridColumnVisibility(columns: ColumnType[]) {
        const columnsCloned = this.objectExtensionService.clone(columns) as ColumnType[];
        if (!this.gridApi) {
            throw new Error('The grid does not exist');
        }

        const columnState: ColumnState[] = this.gridApi.getColumnState();

        for (const state of columnState) {
            const columnFound = columnsCloned.find((column) => column.propertyName === state.colId);
            if (columnFound) {
                state.hide = !columnFound.isSelected;
            }
        }

        this.gridApi.applyColumnState({ state: columnState });
        this.setColumnVisibilityState(columnsCloned);
        this.lastAppliedState.set(this.objectExtensionService.clone(columnsCloned) as ColumnType[]);
        this.gridService.adjustColumnWidths();
    }

    setColumnVisibilityState(columns: ColumnType[]) {
        this.columnVisibilityState.set(columns);
    }

    syncFromGrid() {
        if (!this.gridApi) {
            return;
        }

        const columnState: ColumnState[] = this.gridApi.getColumnState();
        const availableColumns = this.availableColumns();

        const updatedColumns = availableColumns.map((column) => {
            const state = columnState.find((s) => s.colId === column.propertyName);
            return {
                ...column,
                isSelected: state ? !state.hide : column.isSelected
            };
        });

        this.setColumnVisibilityState(updatedColumns);
        this.lastAppliedState.set(this.objectExtensionService.clone(updatedColumns) as ColumnType[]);
    }
}
